// Review and publish. Sync finds documents in the chosen sources; an HR admin reads what was
// extracted and approves it (or excludes the document); Publish makes approved documents the
// set Ask HR answers from. Nothing reaches employees until it is published.
//
// Inclusion (read in full with every question, or only the passages a question needs) is shown to
// everyone here and changed only by HR.Admin; the API enforces that.
//
// The pieces: PublishPanel (the version in use and what's ready), DocumentsTable (the list),
// DocumentPanel (one document's side panel), DocumentFilters (the popover rows and chips), and
// documents.ts for the rules and wording that need no React.

import { useEffect, useState } from "react";
import { INCLUSION_LABELS, MAX_VARIANT_GROUP_CHARS, inclusionOf, type Inclusion, type SyncSummary } from "@ask-hr/core/documents";
import { APPLICABILITY_LABELS, type ApplicabilityValue, type SharePointSource } from "@ask-hr/core/sources";
import { isHidden } from "@ask-hr/core/visibility";
import { ApiError, api } from "../api.js";
import { ErrorNotice } from "../components/ErrorNotice.js";
import { Seg } from "../components/Seg.js";
import { ApplicabilityOptions } from "./ApplicabilityOptions.js";
import { ChoiceRow, FilterChips } from "./DocumentFilters.js";
import { DocumentPanel } from "./DocumentPanel.js";
import { DocumentsTable } from "./DocumentsTable.js";
import {
  DOC_DESC_FIRST,
  FILTERS,
  GROUPED_KEY,
  INCLUSIONS,
  MORE,
  NO_FILTERS,
  PERMISSION_FILTER_LABELS,
  TABS,
  docPath,
  effectiveApplies,
  explainDocumentsError,
  filterDocs,
  groupBySource,
  linkedFilters,
  permissionsOf,
  rangeSelect,
  readGrouped,
  sortDocs,
  when,
  type Doc,
  type DocFilters,
  type DocSortKey,
  type Filter,
  type PermissionFilter,
} from "./documents.js";
import { NeedsAttention } from "./NeedsAttention.js";
import { PublishPanel, type CorpusInfo } from "./PublishPanel.js";
import { useSort } from "./sort.js";
import { label } from "./sources.js";
import { VariantGroups } from "./VariantGroups.js";

interface SyncInfo {
  last: SyncSummary | null;
  running: { trigger: string; startedAt: string } | null;
}

// Filters a link brought in the address (Sources links here), read once; the address is then
// put back to plain /admin/documents so a reload doesn't bring them back.
function linkedFiltersOnce() {
  const linked = linkedFilters(window.location.search);
  if (window.location.search) window.history.replaceState(null, "", window.location.pathname);
  return linked;
}

// isAdmin: HR.Admin, who also chooses sources and sets inclusion. The API checks it either way.
export function DocumentsPage({ isAdmin }: { isAdmin: boolean }) {
  const [docs, setDocs] = useState<Doc[] | null>(null);
  const [sources, setSources] = useState<SharePointSource[]>([]);
  const [corpus, setCorpus] = useState<CorpusInfo | null>(null);
  const [syncInfo, setSyncInfo] = useState<SyncInfo | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [status, setStatus] = useState("");
  // A link from Sources opens the list already filtered (?source=…&tab=all&permissions=hidden).
  const [linked] = useState(linkedFiltersOnce);
  const [filter, setFilterState] = useState<Filter>(linked.tab);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // The row last clicked, where a shift-click's range starts.
  const [anchor, setAnchor] = useState<string | null>(null);
  const [colFilters, setColFiltersState] = useState<DocFilters>(linked.filters);
  const setColFilter = (k: keyof DocFilters, v: string) => {
    setColFiltersState((f) => ({ ...f, [k]: v }));
    setSelected(new Set());
    setAnchor(null);
  };
  const [bulkAudience, setBulkAudience] = useState("");
  const [bulkInclusion, setBulkInclusion] = useState("");
  const [grouping, setGrouping] = useState(false);
  const [groupName, setGroupName] = useState("");
  const setFilter = (f: Filter) => {
    setFilterState(f);
    setSelected(new Set());
    setAnchor(null);
    setGrouping(false);
  };
  // The document whose side panel is open.
  const [open, setOpen] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [grouped, setGroupedState] = useState(() => readGrouped(globalThis.localStorage));
  const setGrouped = (on: boolean) => {
    setGroupedState(on);
    try {
      localStorage.setItem(GROUPED_KEY, on ? "1" : "0");
    } catch {}
  };
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [sort, sortBy] = useSort<DocSortKey>(DOC_DESC_FIRST);

  const fetchAll = () =>
    Promise.all([
      api<{ documents: Doc[] }>("GET", "/api/admin/documents"),
      api<{ sources: SharePointSource[] }>("GET", "/api/admin/sources"),
      api<CorpusInfo>("GET", "/api/admin/corpus"),
      api<SyncInfo>("GET", "/api/admin/sync"),
    ]);

  const apply = ([d, s, c, y]: Awaited<ReturnType<typeof fetchAll>>) => {
    setDocs(d.documents);
    setSources(s.sources);
    setCorpus(c);
    setSyncInfo(y);
    setError(null);
  };

  const reload = async () => {
    try {
      apply(await fetchAll());
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    let live = true;
    fetchAll().then(
      (r) => live && apply(r),
      (e: unknown) => live && setError(e),
    );
    return () => {
      live = false;
    };
    // First load only; later loads go through reload().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While a sync runs, check every 5 seconds, then refresh the list when it finishes.
  const running = Boolean(syncInfo?.running);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      api<SyncInfo>("GET", "/api/admin/sync").then(
        (y) => {
          setSyncInfo(y);
          if (!y.running) void reload();
        },
        () => {
          // Stop asking rather than spin forever; the next reload shows how the sync ended.
          setSyncInfo((y) => (y ? { ...y, running: null } : y));
          setStatus("Couldn't check whether the sync finished. Reload the page to see its result.");
        },
      );
    }, 5000);
    return () => clearInterval(t);
    // reload is recreated each render; the interval only needs the running flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  async function act(key: string, fn: () => Promise<unknown>, done: string) {
    setBusy(key);
    setStatus("");
    try {
      await fn();
      setStatus(done);
      await reload();
    } catch (e) {
      if (e instanceof ApiError && e.body.error === "changed_since_review") {
        setStatus("This document changed in SharePoint since you opened it. Review the new version.");
        setOpen(null);
        await reload();
      } else if (e instanceof ApiError && e.body.error === "already_running") {
        setStatus("A sync is already running.");
        await reload();
      } else {
        setError(e);
      }
    } finally {
      setBusy(null);
    }
  }

  const sourceOf = new Map(sources.map((s) => [s.id, s]));
  // Variant groups: their names (to pick from), and whether each one's versions actually differ in
  // who they apply to. All the same means nobody gets a different version: worth a warning.
  const groupAudiences = new Map<string, Set<string>>();
  for (const d of docs ?? []) {
    if (!d.variantGroup || d.status === "removed") continue;
    const who = d.applicability ?? sourceOf.get(d.sourceId)?.applicability ?? "";
    groupAudiences.set(d.variantGroup, (groupAudiences.get(d.variantGroup) ?? new Set()).add(who));
  }
  const groupNames = [...groupAudiences.keys()].sort((a, b) => a.localeCompare(b));
  const sameAudience = (d: Doc) => (groupAudiences.get(d.variantGroup ?? "")?.size ?? 0) < 2;
  // The toolbar's column filters narrow everything below them, the tab counts included.
  const filtered = filterDocs(docs ?? [], colFilters, sourceOf);
  const counts = Object.fromEntries(
    (Object.keys(FILTERS) as Filter[]).map((f) => [f, filtered.filter(FILTERS[f]).length]),
  ) as Record<Filter, number>;
  // Each menu offers only what the documents have.
  const live = (docs ?? []).filter(FILTERS.all);
  const sourceChoices = sources
    .filter((s) => live.some((d) => d.sourceId === s.id))
    .map((s) => [s.id, label(s)] as const)
    .sort((a, b) => a[1].localeCompare(b[1]));
  const appliesChoices = (Object.keys(APPLICABILITY_LABELS) as ApplicabilityValue[]).filter((v) =>
    live.some((d) => effectiveApplies(d, sourceOf) === v),
  );
  const permissionChoices = (["hidden", "everyone", "unknown", "unchecked"] as const).filter((p) =>
    live.some((d) => (p === "hidden" ? isHidden(d.visibility) : permissionsOf(d) === p)),
  );
  const includedChoices = INCLUSIONS.filter(([v]) => live.some((d) => inclusionOf(d) === v));
  const onTab = TABS.includes(filter);
  const activeFilters = [colFilters.source, colFilters.applies, colFilters.included, colFilters.permissions].filter(Boolean).length + (onTab ? 0 : 1);
  const clearFilters = () => {
    setColFiltersState(NO_FILTERS);
    if (!onTab) setFilterState("all");
    setSelected(new Set());
    setAnchor(null);
  };
  const q = query.trim().toLowerCase();
  const shown = sortDocs(
    filtered
      .filter(FILTERS[filter])
      .filter((d) => !q || `${d.name} ${d.driveName}${d.path} ${d.variantGroup ?? ""}`.toLowerCase().includes(q))
      .sort((a, b) => docPath(a).localeCompare(docPath(b))),
    sort,
    sourceOf,
  );
  // Grouped by source: the sources in name order (a removed source last), each with its rows in
  // the chosen sort. A collapsed group keeps its header and hides its rows.
  const groups = grouped ? groupBySource(shown, sourceOf) : null;
  const rows = groups ? groups.flatMap((g) => (collapsed.has(g.key) ? [] : g.docs)) : shown;
  const chosen = shown.filter((d) => selected.has(d.id));
  const approvable = chosen.filter((d) => (d.status === "new" || d.status === "changed") && d.current);
  const allShownSelected = rows.length > 0 && rows.every((d) => selected.has(d.id));
  // A click toggles one row; a shift-click sets every row from the last one clicked to this one,
  // in the order on screen.
  const toggle = (id: string, range: boolean) => {
    setSelected((cur) => rangeSelect(cur, rows.map((d) => d.id), range ? anchor : null, id));
    setAnchor(id);
  };
  const toggleGroup = (key: string) =>
    setCollapsed((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // The summary line over the list: counts of everything, whatever is filtered.
  const everything = (docs ?? []).filter(FILTERS.all);
  const needReview = (docs ?? []).filter(FILTERS.review).length;
  const cantOpen = everything.filter((d) => isHidden(d.visibility)).length;
  const lastChecked = everything.reduce((a, d) => (d.visibility && d.visibility.checkedAt > a ? d.visibility.checkedAt : a), "");
  const panelDoc = open ? (docs ?? []).find((d) => d.id === open) : undefined;

  async function bulk(
    action: "approve" | "exclude" | "include" | "applicability" | "group" | "inclusion",
    applicability?: ApplicabilityValue | null,
    variantGroup?: string | null,
    inclusion?: Inclusion,
  ) {
    const targets = action === "approve" ? approvable : chosen;
    if (!targets.length) return;
    setBusy("bulk");
    setStatus("");
    try {
      const r = await api<{ results: { id: string; ok: boolean; reason?: string }[] }>("POST", "/api/admin/documents/bulk", {
        action,
        items: targets.map((d) => ({ id: d.id, ...(action === "approve" ? { hash: d.current?.hash } : {}) })),
        ...(action === "applicability" ? { applicability: applicability ?? null } : {}),
        ...(action === "group" ? { variantGroup: variantGroup ?? null } : {}),
        ...(action === "inclusion" ? { inclusion } : {}),
      });
      const done = r.results.filter((x) => x.ok).length;
      const changed = r.results
        .filter((x) => x.reason === "changed_since_review")
        .map((x) => targets.find((d) => d.id === x.id)?.name ?? x.id);
      const verb = {
        approve: "Approved",
        exclude: "Excluded",
        include: "Brought back",
        applicability: "Updated",
        group: variantGroup ? `Grouped as "${variantGroup}":` : "Ungrouped",
        inclusion: `Set coverage to ${INCLUSION_LABELS[inclusion ?? "always"]}:`,
      }[action];
      setStatus(
        `${verb} ${done} ${done === 1 ? "document" : "documents"}.` +
          (changed.length
            ? ` Skipped ${changed.length} that changed in SharePoint since this page loaded: ${changed.join(", ")}. Review those again.`
            : "") +
          (action === "approve" || action === "applicability" || action === "group" || action === "inclusion"
            ? " Publish to make it live."
            : ""),
      );
      // Approved, excluded or brought-back rows leave this tab, so the selection goes with them.
      // Applies to, coverage and grouping leave the rows where they are: keep them selected, so
      // "set Applies to, then Approve" works as one pass.
      if (action === "approve" || action === "exclude" || action === "include") setSelected(new Set());
      setBulkAudience("");
      setBulkInclusion("");
      setGrouping(false);
      setGroupName("");
      await reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <NeedsAttention />
      {error ? <ErrorNotice error={error} explain={explainDocumentsError} /> : null}
      <p className="status" role="status">
        {status}
      </p>

      <PublishPanel corpus={corpus} busy={busy} isAdmin={isAdmin} act={act} />

      <section className="panel" aria-labelledby="sync-h">
        <h2 id="sync-h">Sync sources</h2>
        <div className="toolbar">
          <span>
            {syncInfo?.running ? (
              <>Syncing now (started {when(syncInfo.running.startedAt)})…</>
            ) : syncInfo?.last ? (
              <>
                Last sync {when(syncInfo.last.finishedAt)}
                <span className="muted"> ({syncInfo.last.trigger === "schedule" ? "hourly" : "manual"})</span>: {syncInfo.last.new} new,{" "}
                {syncInfo.last.changed} changed, {syncInfo.last.removed} removed
                {syncInfo.last.errors + syncInfo.last.sourceErrors ? `, ${syncInfo.last.errors + syncInfo.last.sourceErrors} problems` : ""}
              </>
            ) : (
              <span className="muted">No sync has run yet. It runs every hour, or now.</span>
            )}
          </span>
          <button
            type="button"
            disabled={busy !== null || running}
            onClick={() => void act("sync", () => api("POST", "/api/admin/sync"), "Sync started. This page updates when it finishes.")}
          >
            {running ? "Syncing…" : "Sync now"}
          </button>
        </div>
      </section>

      {docs ? (
        <VariantGroups
          docs={docs}
          sources={sources}
          onShow={(group) => {
            setFilter("all");
            setQuery(group);
            document.getElementById("docs-h")?.scrollIntoView({ block: "start" });
          }}
        />
      ) : null}

      <section className="panel" aria-labelledby="docs-h">
        <div className="dochead">
          <h2 id="docs-h">Documents</h2>
          {docs ? (
            <p className="facts">
              <span>
                <strong>{everything.length}</strong> documents
              </span>
              {needReview ? (
                <button type="button" className="linkbtn" onClick={() => setFilter("review")}>
                  {needReview} need review
                </button>
              ) : null}
              {cantOpen ? (
                <button
                  type="button"
                  className="linkbtn"
                  onClick={() => {
                    setFilter("all");
                    setColFilter("permissions", "hidden");
                  }}
                >
                  {cantOpen} employees can't open
                </button>
              ) : null}
              {lastChecked ? <span>Permissions checked {when(lastChecked)}</span> : null}
            </p>
          ) : null}
        </div>

        {chosen.length ? (
          <div className="bulkbar floating" role="region" aria-label="Actions for selected documents">
            <strong>{chosen.length} selected</strong>
            <button type="button" className="primary" disabled={busy !== null || !approvable.length} onClick={() => void bulk("approve")}>
              {approvable.length && approvable.length !== chosen.length ? `Approve ${approvable.length} needing review` : "Approve"}
            </button>
            <button type="button" disabled={busy !== null} onClick={() => void bulk("exclude")}>
              Exclude
            </button>
            {chosen.some((d) => d.status === "excluded") ? (
              <button type="button" disabled={busy !== null} onClick={() => void bulk("include")}>
                Include again
              </button>
            ) : null}
            <label>
              <span className="sr-only">Set who the selected documents apply to</span>
              <select
                value={bulkAudience}
                disabled={busy !== null}
                onChange={(e) => {
                  const v = e.target.value;
                  setBulkAudience(v);
                  if (v) void bulk("applicability", v === "source" ? null : (v as ApplicabilityValue));
                }}
              >
                <option value="">Set applies to…</option>
                <option value="source">Same as source</option>
                <ApplicabilityOptions />
              </select>
            </label>
            {isAdmin ? (
              <label>
                <span className="sr-only">Set the coverage of the selected documents</span>
                <select
                  value={bulkInclusion}
                  disabled={busy !== null}
                  onChange={(e) => {
                    const v = e.target.value as Inclusion | "";
                    setBulkInclusion(v);
                    if (v) void bulk("inclusion", undefined, undefined, v);
                  }}
                >
                  <option value="">Set coverage…</option>
                  {INCLUSIONS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <button type="button" disabled={busy !== null} aria-expanded={grouping} onClick={() => setGrouping(!grouping)}>
              Group as location variants…
            </button>
            {chosen.some((d) => d.variantGroup) ? (
              <button type="button" disabled={busy !== null} onClick={() => void bulk("group", undefined, null)}>
                Ungroup
              </button>
            ) : null}
            <button type="button" className="linkbtn" onClick={() => setSelected(new Set())}>
              Clear selection
            </button>
            {grouping ? (
              <form
                className="group-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (groupName.trim()) void bulk("group", undefined, groupName.trim());
                }}
              >
                <p className="sub">
                  Versions of one document for different places, like a general and a California employee guide. Each employee
                  gets only the version for where they work; one set to All employees is the version for everyone else. Set each
                  version's Applies to, then publish.
                </p>
                <label>
                  <span className="sr-only">Group name</span>
                  <input
                    list="variant-groups"
                    placeholder="Group name, e.g. Employee Guide"
                    maxLength={MAX_VARIANT_GROUP_CHARS}
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    autoFocus
                  />
                </label>
                <datalist id="variant-groups">
                  {groupNames.map((g) => (
                    <option key={g} value={g} />
                  ))}
                </datalist>
                <button type="submit" className="primary" disabled={busy !== null || !groupName.trim()}>
                  Group {chosen.length}
                </button>
                <button type="button" onClick={() => setGrouping(false)}>
                  Cancel
                </button>
              </form>
            ) : null}
          </div>
        ) : null}
        <div className="toolbar">
          <Seg
            label="Show"
            value={filter}
            onChange={setFilter}
            options={[
              ["review", `Needs review (${counts.review})`],
              ["approved", `Approved (${counts.approved})`],
              ["all", `All (${counts.all})`],
            ]}
          />
          <label className="grow">
            <span className="sr-only">Search documents</span>
            <input type="search" placeholder="Search by title or folder" value={query} onChange={(e) => setQuery(e.target.value)} />
          </label>
          <div className="popwrap">
            <button type="button" className="fbtn" aria-expanded={filtersOpen} onClick={() => setFiltersOpen(!filtersOpen)}>
              Filters
              {activeFilters ? <span className="n">{activeFilters}</span> : null}
            </button>
            {filtersOpen ? (
              <div className="popover" role="group" aria-label="Filters">
                <label className="pfield">
                  Source
                  <select value={colFilters.source} onChange={(e) => setColFilter("source", e.target.value)}>
                    <option value="">All sources</option>
                    {sourceChoices.map(([id, name]) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="pfield">
                  Applies to
                  <select value={colFilters.applies} onChange={(e) => setColFilter("applies", e.target.value)}>
                    <option value="">Any</option>
                    {appliesChoices.map((v) => (
                      <option key={v} value={v}>
                        {APPLICABILITY_LABELS[v]}
                      </option>
                    ))}
                  </select>
                </label>
                <ChoiceRow label="Coverage" value={colFilters.included} options={includedChoices} onChange={(v) => setColFilter("included", v)} />
                <ChoiceRow
                  label="Permissions"
                  value={colFilters.permissions}
                  options={permissionChoices.map((p) => [p, PERMISSION_FILTER_LABELS[p]] as const)}
                  onChange={(v) => setColFilter("permissions", v)}
                />
                <ChoiceRow
                  label="Also show"
                  value={onTab ? "" : filter}
                  options={MORE.map(([f, l]) => [f, `${l} (${counts[f]})`] as const)}
                  onChange={(v) => setFilter((v || "all") as Filter)}
                />
                <div className="actions">
                  <button type="button" className="linkbtn" onClick={clearFilters}>
                    Clear all
                  </button>
                  <button type="button" className="primary" onClick={() => setFiltersOpen(false)}>
                    Done
                  </button>
                </div>
              </div>
            ) : null}
          </div>
          <label className="small muted nowrap">
            <input type="checkbox" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} /> Group by source
          </label>
        </div>

        <FilterChips
          chips={[
            ...(colFilters.source
              ? [["source", `Source: ${sourceChoices.find(([id]) => id === colFilters.source)?.[1] ?? "removed"}`] as const]
              : []),
            ...(colFilters.applies
              ? [["applies", `Applies to: ${APPLICABILITY_LABELS[colFilters.applies as ApplicabilityValue] ?? colFilters.applies}`] as const]
              : []),
            ...(colFilters.included ? [["included", `Coverage: ${INCLUSION_LABELS[colFilters.included as Inclusion]}`] as const] : []),
            ...(colFilters.permissions
              ? [["permissions", `Permissions: ${PERMISSION_FILTER_LABELS[colFilters.permissions as PermissionFilter] ?? colFilters.permissions}`] as const]
              : []),
            ...(onTab ? [] : [["view", `Showing: ${MORE.find(([f]) => f === filter)?.[1] ?? filter}`] as const]),
          ]}
          onRemove={(k) => (k === "view" ? setFilter("all") : setColFilter(k as keyof DocFilters, ""))}
          onClear={clearFilters}
        />

        {filter === "archive" ? (
          <p className="sub">
            Documents in folders named Archive. They stay out of Needs review; approve one here if Ask HR should use it.
          </p>
        ) : null}

        {docs === null ? (
          error ? null : <p className="sub">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="empty">
            {filter === "review" && !q && !activeFilters
              ? docs.length
                ? "Nothing needs review."
                : isAdmin
                  ? "No documents yet. Choose sources on the Sources tab, then sync."
                  : "No documents yet. An HR admin chooses sources on the Sources tab; then sync."
              : q
                ? `Nothing matches "${query.trim()}".`
                : filter === "archive"
                  ? "No documents in Archive folders."
                  : "Nothing matches these filters."}
          </p>
        ) : (
          <DocumentsTable
            groups={groups}
            shown={shown}
            rows={rows}
            sourceOf={sourceOf}
            selected={selected}
            open={open}
            collapsed={collapsed}
            sort={sort}
            onSort={sortBy}
            sameAudience={sameAudience}
            onSelectAll={() => setSelected(allShownSelected ? new Set() : new Set(rows.map((d) => d.id)))}
            onToggle={toggle}
            onToggleGroup={toggleGroup}
            onOpen={setOpen}
          />
        )}
      </section>

      {panelDoc ? (
        <DocumentPanel
          key={panelDoc.id}
          doc={panelDoc}
          src={sourceOf.get(panelDoc.sourceId)}
          isAdmin={isAdmin}
          busy={busy}
          sameAudience={sameAudience(panelDoc)}
          onClose={() => setOpen(null)}
          onShowGroup={(g) => {
            setOpen(null);
            setFilter("all");
            setQuery(g);
          }}
          act={(fn, done) => void act(panelDoc.id, fn, done)}
        />
      ) : null}
    </>
  );
}
